import { supabase } from "@/integrations/supabase/client";
import type { TemplateFormAttachment } from "./templateForm";
import type { ResolvedAttachment } from "./templateFormPdfAttachments";

/** Long enough to fetch the object, short enough not to be worth passing on. */
const SIGNED_URL_TTL_SECONDS = 60;

async function projectDocumentBytes(documentId: string): Promise<Uint8Array | null> {
  const { data: doc, error } = await supabase
    .from("documents")
    .select("file_path")
    .eq("id", Number(documentId))
    .maybeSingle();
  if (error || !doc?.file_path) return null;

  const { data: signed } = await supabase.storage
    .from("documents")
    .createSignedUrl(doc.file_path, SIGNED_URL_TTL_SECONDS);
  if (!signed?.signedUrl) return null;

  const response = await fetch(signed.signedUrl);
  if (!response.ok) return null;
  return new Uint8Array(await response.arrayBuffer());
}

export interface ResolvedAttachments {
  resolved: ResolvedAttachment[];
  /**
   * Attachments whose bytes could not be had, by name.
   *
   * Reported, not silently dropped. These never reach `appendAttachmentsToPdf`,
   * which is the only thing that fills `notAppended`, so before this they left
   * no trace at all: the user ticked "add attachments to the PDF", got "Form
   * saved", and the statutory document went out without the evidence it names.
   */
  unresolved: string[];
}

/**
 * The bytes behind each attachment, whichever way it got onto the form — a file
 * staged from the device still holds its `File`, one picked from the project has
 * to be fetched back out of storage.
 *
 * An attachment whose bytes cannot be had does not fail the save: it is still
 * listed on the form and still attached to the record, it just does not
 * contribute pages — and the caller says so.
 */
export async function resolveAttachmentBytes(
  attachments: TemplateFormAttachment[]
): Promise<ResolvedAttachments> {
  const resolved: ResolvedAttachment[] = [];
  const unresolved: string[] = [];
  for (const attachment of attachments) {
    if (attachment.file) {
      resolved.push({
        name: attachment.name,
        bytes: new Uint8Array(await attachment.file.arrayBuffer()),
      });
      continue;
    }
    // Named on the form but pointing at no document at all — it can never
    // contribute pages, so it belongs in the same report as a failed fetch.
    if (!attachment.documentId) {
      unresolved.push(attachment.name);
      continue;
    }
    const bytes = await projectDocumentBytes(attachment.documentId);
    if (bytes) resolved.push({ name: attachment.name, bytes });
    else unresolved.push(attachment.name);
  }
  return { resolved, unresolved };
}
